import { truncateString } from "@/v2-common/utils/utils.formatting"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import InfoGrid, {
  type InfoGridProps,
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import { getUserEntraUrl } from "@/v2-backoffice-client/user/UserEntraButton"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import type { UserActiveProjectFragment } from "@/v2-backoffice-client/user/activeProject/__types__/user.activeProject.fragment"
import UserStatusBadge from "@/v2-backoffice-client/user/status/UserStatusBadge"
import UserActivityBadge from "@/v2-backoffice-client/user/activity/UserActivityBadge"
import UserIdentityCell from "@/v2-backoffice-client/user/identity/UserIdentityCell"
import UserMigrationCell from "@/v2-backoffice-client/user/migration/UserMigrationCell"
import UserContactSyncCell from "@/v2-backoffice-client/user/contact/UserContactSyncCell"
import CountryLabel from "@/v2-backoffice-client/country/CountryLabel"
import {
  getUserActiveLanguageCode,
  getUserLanguageLabel,
} from "@/v2-backoffice-client/user/user.constants"
import {
  getUserContactLifecycleLabel,
  getUserContactLeadStatusLabel,
} from "@/v2-backoffice-client/user/contact/userContact.constants"
import { getUserHubspotContactUrl } from "@/v2-backoffice-client/user/UserHubspotButton"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import OutsideEeaBadge from "@/v2-backoffice-client/signIn/outsideEea/OutsideEeaBadge"
import { useUserOutsideEeaQuery } from "@/v2-backoffice-client/user/outsideEea/user.outsideEea.query"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import {
  formatUserLicenseSeats,
  type UserLicenseSeat,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"
import UserSeatBadges from "@/v2-backoffice-client/user/seats/UserSeatBadges"
import { useUserLicenseSeats } from "@/v2-backoffice-client/user/seats/user.seats.hooks"
import { formatUserFirstName } from "@/v2-backoffice-client/user/user.utils"

// The info grid is fed by getUserById; the outside-EEA flag is loaded
// separately from the durable sign-in store (a fast, all-time lookup) and
// merged in so it can render as an ordinary grid field. LicenseStatusName
// is resolved from the getLicenseStatuses lookup the same way, and
// LicenseSeats from the owning customer's licenses and seat holders.
type UserViewInfoUser = UserFragment & UserActiveProjectFragment
type UserViewInfoData = UserViewInfoUser & {
  OutsideEeaCountries: string[]
  LicenseStatusName?: string
  LicenseSeats: UserLicenseSeat[]
}

const FIELDS = createInfoGridFields<UserViewInfoData>({
  fields: {
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
    FirstName: {
      label: "First Name",
      accessor: "FirstName",
      defaultHidden: true,
    },
    LastName: {
      label: "Last Name",
      accessor: "LastName",
      defaultHidden: true,
    },
    UserStatus: {
      label: "Account Status",
      accessor: "UserStatusCode",
      renderValue({ data }) {
        return <UserStatusBadge status={data.UserStatus} />
      },
    },
    UserActivityLevel: {
      label: "Activity Level",
      accessor: "UserActivityLevelCode",
      renderValue({ data }) {
        return <UserActivityBadge level={data.UserActivityLevel} />
      },
    },
    OutsideEea: {
      label: "Outside EU/EEA",
      accessor: (data) => data.OutsideEeaCountries,
      // Only surfaced when the user has actually signed in from outside the
      // EEA — keeps the grid clean for the common (compliant) case.
      show: (data) => data.OutsideEeaCountries.length > 0,
      renderValue({ data }) {
        return <OutsideEeaBadge countries={data.OutsideEeaCountries} />
      },
    },
    Email: {
      label: "Email",
      accessor: "Email",
      valueClassName: "truncate",
    },
    Language: {
      label: "Language",
      accessor: (data) => getUserActiveLanguageCode(data.Configuration),
      renderValue({ data }) {
        const code = getUserActiveLanguageCode(data.Configuration)
        if (!code) {
          return <span className="text-muted-foreground">App default</span>
        }
        return (
          <span title={code}>
            {getUserLanguageLabel(code)}{" "}
            <span className="text-muted-foreground">({code})</span>
          </span>
        )
      },
    },
    // Active workspace context (Configuration.ActiveTenantId /
    // .ActiveProjectId) — what the console loads on the user's next
    // session. Set via the Update dialog.
    ActiveTenant: {
      label: "Active Team",
      accessor: (data) => data.ActiveTenant?.Name ?? data.ActiveTenantId,
      to: ({ data }) =>
        data.ActiveTenant ? `?v=tenant:${data.ActiveTenant.Id}` : undefined,
      renderValue({ data }) {
        if (!data.ActiveTenantId) {
          return <span className="text-muted-foreground">Not set</span>
        }
        // The id points at a tenant that no longer resolves (removed) —
        // show the raw id so the stale state is visible.
        if (!data.ActiveTenant) {
          return (
            <span className="text-muted-foreground">
              #{data.ActiveTenantId} (unknown team)
            </span>
          )
        }
        return data.ActiveTenant.Name
      },
    },
    ActiveProject: {
      label: "Active Project",
      accessor: (data) => data.ActiveProject?.Name ?? data.ActiveProjectId,
      renderValue({ data }) {
        if (!data.ActiveProjectId) {
          return <span className="text-muted-foreground">Not set</span>
        }
        if (!data.ActiveProject) {
          return (
            <span className="text-muted-foreground">
              #{data.ActiveProjectId} (unknown project)
            </span>
          )
        }
        return (
          <span>
            {data.ActiveProject.Name}
            {data.ActiveProject.IsDeleted && (
              <span className="text-destructive"> (deleted)</span>
            )}
          </span>
        )
      },
    },
    ActiveCountry: {
      label: "Active Country",
      accessor: "ActiveCountryCode",
      renderValue: ({ data }) => (
        <CountryLabel
          code={data.ActiveCountryCode}
          // Saying where the value comes from matters more than the value:
          // a country that disagrees with the active project is a bug the
          // operator should be able to spot at a glance.
          emptyLabel={data.ActiveProjectId ? "Follows the project" : "Not set"}
        />
      ),
    },
    CustomerNames: {
      label: "Customer",
      description:
        "The customer that owns the user: whose licenses and seats apply to them. Normally the customer of the user's teams; the toolbar's Customer button moves them.",
      accessor: (data) => data.CustomerNames?.join(", "),
      renderValue({ data }) {
        if (!data.Customers?.length) return
        return (
          <TruncatedList
            items={data.Customers.map((customer) => ({
              label: customer.Name,
              to: `?v=customer:${customer.ExternalCustomerId}`,
            }))}
          />
        )
      },
    },
    // License posture of the owning customer: the governing status with the
    // trial countdown, and the held licenses. Only surfaced when the user
    // has an owning customer — license tracking doesn't apply otherwise.
    License: {
      label: "License status",
      accessor: "LicenseStatusCode",
      show: (data) => data.LicenseStatusCode != null,
      renderValue({ data }) {
        return (
          <LicenseGoverningBadge
            license={data}
            statusName={data.LicenseStatusName}
          />
        )
      },
    },
    LicensePlanNames: {
      label: "Licenses",
      description:
        "The licenses the owning customer holds. A seated license limited to named people reaches only its seat holders: the Seats field says whether this user is one.",
      accessor: "LicensePlanNames",
      show: (data) => Boolean(data.LicensePlanNames),
    },
    // Where this one person stands on the customer's seated licenses (the
    // MCP servers): the single piece of their access that is theirs
    // alone. Only surfaced while the customer holds a live seated license.
    LicenseSeats: {
      label: "Seats",
      description:
        "Seats on the owning customer's seated licenses (the MCP servers). A license limited to named people reaches only them, and a seat is what lets this user use it; one that is not limited reaches everyone at the customer. Named on the customer panel's Licenses & add-ons tab (row menu, Seats).",
      accessor: (data) => formatUserLicenseSeats(data.LicenseSeats),
      show: (data) => data.LicenseSeats.length > 0,
      renderValue({ data }) {
        return (
          <UserSeatBadges
            seats={data.LicenseSeats}
            userName={formatUserFirstName(data)}
            customerName={data.Customer?.Name ?? "their customer"}
          />
        )
      },
    },
    // The HubSpot link state, always rendered (healthy included) like the
    // Identity field below — its tooltip carries the "Sync to HubSpot"
    // action for unlinked users.
    ContactSyncStatus: {
      label: "HubSpot",
      accessor: "ContactSyncStatus",
      renderValue({ data }) {
        return <UserContactSyncCell user={data} showHealthy />
      },
    },
    // CRM snapshot (acc.UserContacts, HubSpot-owned): read-only display of
    // where the user is in the sales/CS funnel. Only rendered once the CRM
    // sync has linked the user to a contact; workflow happens in HubSpot
    // (the toolbar's HubSpot button deep-links to the contact record).
    CrmOwner: {
      label: "CRM Owner",
      accessor: (data) => data.Contact?.OwnerName,
      show: (data) => Boolean(data.Contact),
      to: ({ data }) =>
        getUserHubspotContactUrl(data.Contact?.ExternalContactId),
    },
    CrmLifecycleStage: {
      label: "Lifecycle Stage",
      accessor: (data) => data.Contact?.LifecycleStage,
      show: (data) => Boolean(data.Contact),
      renderValue({ value }) {
        if (!value) return
        return getUserContactLifecycleLabel(String(value))
      },
    },
    CrmLeadStatus: {
      label: "Lead Status",
      accessor: (data) => data.Contact?.LeadStatus,
      show: (data) => Boolean(data.Contact),
      renderValue({ value }) {
        if (!value) return
        return getUserContactLeadStatusLabel(String(value))
      },
    },
    CrmLastContacted: {
      label: "Last Contacted",
      accessor: (data) => data.Contact?.LastContactedAt,
      show: (data) => Boolean(data.Contact),
      renderValue({ data, value }) {
        if (!value) return
        const count = data.Contact?.ContactedCount
        return (
          <span>
            {formatDateString(String(value))}
            {count ? (
              <span className="text-muted-foreground"> ({count}×)</span>
            ) : null}
          </span>
        )
      },
    },
    // The contact email as CRM sees it — may legitimately drift from the app
    // email (sales edits it in HubSpot); hidden unless someone digs.
    CrmEmail: {
      label: "CRM Email",
      accessor: (data) => data.Contact?.Email,
      show: (data) => Boolean(data.Contact),
      defaultHidden: true,
      valueClassName: "truncate",
    },
    CrmSyncedAt: {
      label: "CRM Synced",
      accessor: (data) => data.Contact?.SyncedAt,
      show: (data) => Boolean(data.Contact),
      defaultHidden: true,
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    AzureID: {
      label: "Azure Id",
      accessor: "AzureID",
      defaultHidden: true,
      to: ({ data }) => getUserEntraUrl(data.AzureID),
      renderValue({ value }) {
        if (!value) return
        return truncateString(String(value), 10, "...")
      },
    },
    IdentitySyncStatus: {
      label: "Identity",
      accessor: "IdentitySyncStatus",
      renderValue({ data }) {
        return <UserIdentityCell user={data} showHealthy />
      },
    },
    MigrationStatus: {
      label: "Fusion login",
      accessor: "MigrationStatus",
      renderValue({ data }) {
        return <UserMigrationCell user={data} />
      },
    },
    IdentityCreatedAt: {
      label: "Sign-up",
      accessor: "IdentityCreatedAt",
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    LastSignInAt: {
      label: "Last Sign-in",
      accessor: "LastSignInAt",
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    LatestAppVersion: {
      label: "Latest Version",
      accessor: "LatestAppVersion",
      renderValue({ value }) {
        if (!value) return
        return <span className="font-mono text-xs">{String(value)}</span>
      },
    },
    LastSuccessfulSignInAt: {
      label: "Last Successful Sign-in",
      accessor: "LastSuccessfulSignInAt",
      defaultHidden: true,
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    CreatedAt: {
      label: "Created",
      accessor: "CreatedAt",
      defaultHidden: true,
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
    UpdatedAt: {
      label: "Updated",
      accessor: "UpdatedAt",
      defaultHidden: true,
      renderValue({ value }) {
        if (!value) return
        return formatDateString(String(value))
      },
    },
  },
})

export type UserViewInfoProps = Omit<
  InfoGridProps<UserViewInfoUser>,
  "data" | "fields"
> & {
  user: UserViewInfoUser | null | undefined
}

function UserViewInfo(props: UserViewInfoProps) {
  const { user, className, ...infoGridProps } = props

  // Durable, all-time non-EEA countries for the user — a fast covering seek on
  // the stored sign-in events, so it's safe to load with the always-visible
  // info grid (no live MS Graph fetch). Skipped until the user has loaded.
  const { data: eeaData } = useUserOutsideEeaQuery({
    variables: { userId: user?.Id ?? 0 },
    skip: !user?.Id,
  })

  // Status display name for the License badge, from the seeded lookup the
  // license board also uses (cached, so this is free after the first load).
  const { data: licenseStatusData } = useLicenseStatusesQuery({
    skip: user?.LicenseStatusCode == null,
  })

  // The person's standing on the owning customer's seated licenses; empty
  // (and the field hidden) for a customer without one.
  const licenseSeats = useUserLicenseSeats(user)

  const gridData: UserViewInfoData | null | undefined = user
    ? {
        ...user,
        OutsideEeaCountries: eeaData?.getUserOutsideEeaCountries ?? [],
        LicenseStatusName: licenseStatusData?.getLicenseStatuses.find(
          (status) => status.Code === user.LicenseStatusCode,
        )?.Name,
        LicenseSeats: licenseSeats,
      }
    : user

  return (
    <InfoGrid
      id="user-info"
      className={cn("user-view-info", className)}
      data={gridData}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default UserViewInfo
